import type Badge from "@travily/types/models/BadgesRow";

/** Badges per passport page — both the desktop page (half a spread) and a mobile tile. */
export const PAGE_SIZE = 6;

export interface SectionPage {
  /** Stable section key for the curved title (resolved via i18n). */
  section: Badge["section"];
  /** Up to PAGE_SIZE badges, in the backend's section-first order. */
  badges: Badge[];
  /** True only on the first page of a section's run — the only page that shows the title. */
  showTitle: boolean;
}

/**
 * Splits a flat, SECTION-FIRST-ordered badge array (as `passport_sectioned` ships it)
 * into PAGE_SIZE-badge pages where every section starts on a fresh page and its curved
 * title renders once (on that first page). Continuation pages of the same section are
 * untitled; a section's last page keeps its trailing empty slots (the next section never
 * bleeds into them, because each run is chunked independently).
 *
 * Pure + framework-free: PassportBook pairs pages into two-page spreads, PassportNotepad
 * maps one page per tile. A section with zero badges produces zero pages, so no empty
 * section header is ever rendered.
 */
export function paginateBySection(badges: readonly Badge[]): SectionPage[] {
  const pages: SectionPage[] = [];
  let index = 0;

  while (index < badges.length) {
    const first = badges[index];
    // Unreachable given the loop bound, but satisfies noUncheckedIndexedAccess.
    if (!first) break;

    const { section } = first;
    const runStart = index;
    index += 1;
    // Extend the run while the section key matches (the array is already grouped).
    while (index < badges.length && badges[index]?.section === section) index += 1;

    // Chunk this section's run into pages; only the first page carries the title.
    for (let offset = runStart; offset < index; offset += PAGE_SIZE) {
      pages.push({
        section,
        badges: badges.slice(offset, Math.min(offset + PAGE_SIZE, index)),
        showTitle: offset === runStart,
      });
    }
  }

  return pages;
}
